import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  Filter,
  RotateCcw,
  Info,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FileBarChart2,
  CalendarRange,
  Database,
  TrendingUp,
  Wallet,
  Hash,
  Layers,
  Package,
  Truck,
  CreditCard,
  Tag,
  XCircle,
  History,
  Clock,
  CheckCircle,
  Sparkles,
  Download,
} from "lucide-react";
import ExcelIcon from "../components/ExcelIcon";
import { BadgePercent } from "lucide-react";
import PdfIcon from "../components/PdfIcon";
import { exportProfitSalesToExcel } from "../utils/profitSalesExcelExport";
import { exportProfitSalesToPdf } from "../utils/profitSalesPdfExport";
import ProfitSalesReportTab from "../components/reports/ProfitSalesReportTab";

import { exportReportToExcel } from "../utils/reportExcelExport";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import ReportTransactionDetailModal from "../components/reports/ReportTransactionDetailModal";
import ReportShiftDetailModal from "../components/reports/ReportShiftDetailModal";
import SalesTypeRecapTable from "../components/reports/SalesTypeRecapTable";
import PaymentRecapTable from "../components/reports/PaymentRecapTable";
import SearchableSelect from "../components/SearchableSelect";
import { reportService } from "../api/reportService";
import { masterService, salesTypeService } from "../api/masterService";
import { useNotification } from "../contexts/NotificationContext";

const REPORTS = [
  {
    key: "transaction-history",
    label: "Riwayat Transaksi",
    icon: History,
    accent: "teal",
  },
  {
    key: "shifts",
    label: "Riwayat Shift",
    icon: Clock,
    accent: "orange",
  },
  {
    key: "sales",
    label: "Laporan Bulanan",
    icon: CalendarRange,
    accent: "indigo",
  },
  {
    key: "profit-loss",
    label: "Laba/Rugi",
    icon: TrendingUp,
    accent: "emerald",
  },
  {
    key: "profit-sales",
    label: "Profit Penjualan",
    icon: BadgePercent,
    accent: "teal",
  },
  {
    key: "recap-products",
    label: "Rekap Produk",
    icon: Package,
    accent: "emerald",
  },
  {
    key: "recap-units",
    label: "Laporan Per Unit",
    icon: Truck,
    accent: "amber",
  },
  {
    key: "recap-categories",
    label: "Rekap Kategori",
    icon: Layers,
    accent: "sky",
  },
  {
    key: "recap-payments",
    label: "Rekap Pembayaran",
    icon: CreditCard,
    accent: "violet",
  },
  {
    key: "recap-sales-types",
    label: "Rekap Tipe Penjualan",
    icon: Tag,
    accent: "pink",
  },
  {
    key: "void-transactions",
    label: "Transaksi Void",
    icon: XCircle,
    accent: "rose",
  },
];

const REPORT_META = REPORTS.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

// Dummy data Laporan Bulanan dihapus — sekarang menggunakan data dari API (sales report).

const DEFAULT_LIMIT = 20;

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatNumber = (value) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed)) return "0";
  return parsed.toLocaleString("id-ID");
};

const formatCurrency = (value) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed)) return "Rp 0";
  return `Rp ${parsed.toLocaleString("id-ID")}`;
};

const toInputDate = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDailyRange = (date = new Date()) => {
  const ymd = toInputDate(date);
  return { startDate: ymd, endDate: ymd };
};

const getWeeklyRange = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 is Sunday, 1 is Monday, ...
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  return {
    startDate: toInputDate(monday),
    endDate: toInputDate(sunday),
  };
};

const getMonthlyRange = (date = new Date()) => {
  const d = new Date(date);
  const firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return {
    startDate: toInputDate(firstDay),
    endDate: toInputDate(lastDay),
  };
};

const getDayName = (d) => {
  const days = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
  return days[d.getDay()];
};

const getMonthName = (d) => {
  const months = [
    "Januari", "Februari", "Maret", "April", "Mei", "Juni",
    "Juli", "Agustus", "September", "Oktober", "November", "Desember"
  ];
  return months[d.getMonth()];
};

const formatPeriodLabel = (mode, anchor, start, end) => {
  if (mode === "all") {
    return "Semua Periode";
  }
  const d = new Date(anchor);
  if (mode === "daily") {
    const dayName = getDayName(d);
    const dateFormatted = d.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    return `${dayName}, ${dateFormatted}`;
  }
  if (mode === "weekly") {
    if (!start || !end) return "-";
    const s = new Date(start);
    const e = new Date(end);
    const sFormatted = s.toLocaleDateString("id-ID", { day: "numeric", month: "short" });
    const eFormatted = e.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
    return `${sFormatted} - ${eFormatted}`;
  }
  if (mode === "monthly") {
    return `${getMonthName(d)} ${d.getFullYear()}`;
  }
  if (!start || !end) return "Kustom";
  const s = new Date(start);
  const e = new Date(end);
  return `${s.toLocaleDateString("id-ID", { day: "numeric", month: "short" })} - ${e.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}`;
};

const getAllTimeRange = (earliest) => {
  const today = new Date();
  const firstDay = earliest ? new Date(earliest) : new Date();
  return {
    startDate: toInputDate(firstDay),
    endDate: toInputDate(today),
  };
};

const getDefaultDateRange = (earliest) => {
  return getAllTimeRange(earliest);
};

const getReportColumns = (reportKey) => {
  switch (reportKey) {
    case "transaction-history":
      return [
        { key: "date", label: "Tanggal", type: "date" },
        { key: "transaction_no", label: "No. Transaksi" },
        { key: "source", label: "Sumber", type: "source" },
        { key: "customer", label: "Pelanggan" },
        { key: "type", label: "Tipe" },
        { key: "sub_total", label: "Sub Total", type: "currency" },
        { key: "other_cost", label: "Biaya Lain", type: "currency" },
        { key: "grand_total", label: "Grand Total", type: "currency" },
      ];
    case "shifts":
      return [
        { key: "shift_number", label: "No. Shift" },
        { key: "status", label: "Status" },
        { key: "opened_by_name", label: "Kasir" },
        { key: "opened_at", label: "Waktu Buka", type: "datetime" },
        { key: "closed_at", label: "Waktu Tutup", type: "datetime" },
        { key: "opening_cash", label: "Saldo Awal", type: "currency" },
        { key: "expected_cash", label: "Ekspektasi Kas", type: "currency" },
        { key: "closing_cash", label: "Kas Aktual", type: "currency" },
        { key: "cash_difference", label: "Selisih Kas", type: "currency" },
      ];
    case "sales":
      return [
        { key: "date", label: "Tanggal", type: "date" },
        { key: "product_name", label: "Nama Produk" },
        { key: "quantity", label: "Jumlah", type: "number" },
        { key: "price", label: "Harga", type: "currency" },
        { key: "sub_total", label: "Sub Total", type: "currency" },
        { key: "total_per_nota", label: "Total / Nota", type: "currency" },
        { key: "unit", label: "Unit" },
        { key: "receiver", label: "Penerima" },
        { key: "total_per_day", label: "Total / Hari", type: "currency" },
      ];
    case "recap-products":
      return [
        { key: "no", label: "No", type: "number" },
        { key: "product_name", label: "Produk" },
        { key: "item_type", label: "Tipe Item" },
        { key: "quantity", label: "Qty", type: "number" },
        { key: "unit", label: "Satuan" },
        { key: "total_sales", label: "Subtotal Penjualan", type: "currency" },
      ];
    case "recap-units":
      return [
        { key: "no", label: "No", type: "number" },
        { key: "unit", label: "Nama Unit" },
        { key: "sparepart", label: "Sparepart" },
        { key: "quantity", label: "Jumlah", type: "number" },
        { key: "satuan", label: "Satuan" },
        { key: "sub_total", label: "Sub Total", type: "currency" },
        { key: "total_pemakaian", label: "Total Pemakaian", type: "currency" },
      ];

    case "recap-categories":
      return [
        { key: "name", label: "Kategori" },
        { key: "transaction_count", label: "Jumlah Transaksi", type: "number" },
        { key: "total_sales", label: "Subtotal Penjualan", type: "currency" },
      ];
    case "recap-payments":
      return [
        { key: "no", label: "No", type: "number" },
        { key: "payment_method", label: "Metode Pembayaran" },
        { key: "transaction_count", label: "Jumlah Transaksi", type: "number" },
        { key: "total_sales", label: "Total Pembayaran", type: "currency" },
      ];
    case "recap-sales-types":
      return [
        { key: "no", label: "No", type: "number" },
        { key: "sales_type", label: "Tipe Penjualan" },
        { key: "transaction_count", label: "Jumlah Transaksi", type: "number" },
        { key: "transaction_ratio", label: "Rasio (%)", type: "number" },
        { key: "total_sales", label: "Pendapatan Bersih", type: "currency" },
      ];
    case "void-transactions":
      return [
        { key: "date", label: "Tanggal", type: "date" },
        { key: "transaction_no", label: "No. Transaksi" },
        { key: "customer", label: "Pelanggan" },
        { key: "type", label: "Tipe" },
        { key: "sub_total", label: "Sub Total", type: "currency" },
        { key: "other_cost", label: "Biaya Lain", type: "currency" },
        { key: "grand_total", label: "Grand Total", type: "currency" },
        { key: "payment_method", label: "Metode Bayar" },
        { key: "created_by", label: "Dibuat Oleh" },
        { key: "canceled_by", label: "Dibatalkan Oleh" },
        { key: "canceled_at", label: "Tanggal Batal", type: "date" },
        { key: "void_description", label: "Alasan Void" },
      ];
    default:
      return [];
  }
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCellValue = (column, value, row) => {
  if (column.key === "product_name" && row) {
    let displayName = String(value || "").trim();
    if (row.product_addon_id) {
      // Clean up the parent product name prefix if it exists in the addon name
      const parts = displayName.split(" - ");
      if (parts.length > 1) {
        displayName = parts[parts.length - 1];
      }
      return (
        <span className="text-slate-500 font-medium italic pl-4 block">
          + {displayName}
        </span>
      );
    }
    
    // Base product details
    const detailParts = [];
    if (row.brand_name) detailParts.push(`Merk: ${row.brand_name}`);
    if (row.variant_name) detailParts.push(`Varian: ${row.variant_name}`);
    
    return (
      <div className="flex flex-col">
        <span className="font-semibold text-slate-800">{displayName}</span>
        {detailParts.length > 0 && (
          <span className="text-[10px] text-slate-500 font-normal mt-0.5 tracking-wide">
            {detailParts.join(" | ")}
          </span>
        )}
      </div>
    );
  }
  
  if (column.key === "unit" && row && row.product_addon_id) {
    return "-";
  }

  if (column.key === "receiver") {
    const val = String(value || "").trim();
    if (!val || val === "-" || val === "null" || val === "undefined") {
      const fallback = row?.customer_name || row?.customer;
      return fallback && String(fallback).trim() && String(fallback).trim() !== "-"
        ? String(fallback).trim()
        : "Pelanggan Umum";
    }
    return val;
  }

  if (column.key === "source") {
    const isOnline = String(value || "").toUpperCase() === "ECATALOG_PUBLIC";
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${
        isOnline ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-800"
      }`}>
        {isOnline ? "E-Katalog" : "Kasir POS"}
      </span>
    );
  }

  if (column.key === "closing_cash" && row) {
    const val = row.closing_cash !== undefined && row.closing_cash !== null ? row.closing_cash : row.actual_cash;
    if (row.status === "OPEN" && (val === 0 || val === null || val === undefined)) {
      return "-";
    }
    if (val !== undefined && val !== null) {
      return formatCurrency(val);
    }
    return "-";
  }

  if (column.key === "cash_difference" && row && (row.shift_number || row.shift_id)) {
    if (row.status === "OPEN") return "-";
    const diff = Number(value || 0);
    return (
      <span className={diff < 0 ? "text-rose-600 font-medium" : diff > 0 ? "text-emerald-600 font-medium" : "text-slate-700"}>
        {formatCurrency(diff)}
      </span>
    );
  }

  if (column.key === "status" && row && (row.shift_number || row.shift_id)) {
    const isClosed = String(value || "").toUpperCase() === "CLOSED";
    return (
      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${
        isClosed ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-amber-50 text-amber-700 ring-amber-200"
      }`}>
        {isClosed ? "Tutup" : "Buka"}
      </span>
    );
  }

  if (value === null || value === undefined || value === "") return "-";
  if (column.type === "currency") return formatCurrency(value);
  if (column.type === "date") return formatDate(value);
  if (column.type === "datetime") return formatDateTime(value);
  if (column.type === "number") return formatNumber(value);
  return String(value);
};

export default function Reports() {
  const { showSuccess, showError, showInfo } = useNotification();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user_data") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isKasir = useMemo(() => {
    try {
      const roleStr = String(
        userData?.role ||
          userData?.role_name ||
          userData?.role_id ||
          userData?.user?.role ||
          localStorage.getItem("role") ||
          "",
      ).toLowerCase();
      const isAdminFlag =
        userData?.is_admin === true ||
        userData?.is_system_admin === true ||
        roleStr.includes("admin") ||
        roleStr.includes("owner");
      return !isAdminFlag;
    } catch {
      return true;
    }
  }, [userData]);

  const isAdmin = !isKasir;

  const visibleReports = useMemo(() => {
    let list = REPORTS;
    if (isKasir) {
      return list.filter(
        (r) => r.key === "transaction-history",
      );
    }
    return list;
  }, [isKasir]);

  const [activeReport, setActiveReport] = useState(REPORTS[0].key);
  const [transactionSourceFilter, setTransactionSourceFilter] = useState("all");
  const [salesTypes, setSalesTypes] = useState([]);
  const [salesTypeFilter, setSalesTypeFilter] = useState("all");

  useEffect(() => {
    if (isKasir && activeReport !== "transaction-history") {
      setActiveReport("transaction-history");
    }
  }, [isKasir, activeReport]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [reportFilterType, setReportFilterType] = useState("all"); // "all", "category", "product"
  const [reportCategoryId, setReportCategoryId] = useState("");
  const [reportProductId, setReportProductId] = useState("");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [dailyTotals, setDailyTotals] = useState([]);
  const [totalUsage, setTotalUsage] = useState(0);
  const [meta, setMeta] = useState({ page: 1, limit: DEFAULT_LIMIT, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profitLossData, setProfitLossData] = useState(null);
  const [profitSalesData, setProfitSalesData] = useState(null);

  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [periodMode, setPeriodMode] = useState("all"); // 'all' | 'daily' | 'weekly' | 'monthly' | 'custom'
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [earliestDate, setEarliestDate] = useState(null);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [hasInitDate, setHasInitDate] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isDesktopFilterOpen, setIsDesktopFilterOpen] = useState(false);
  const desktopFilterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (desktopFilterRef.current && !desktopFilterRef.current.contains(event.target)) {
        setIsDesktopFilterOpen(false);
      }
    };
    if (isDesktopFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isDesktopFilterOpen]);

  const isCurrentPeriod = useMemo(() => {
    if (periodMode === "all") return true;
    const now = new Date();
    if (periodMode === "daily") {
      return toInputDate(anchorDate) === toInputDate(now);
    }
    if (periodMode === "weekly") {
      const currentWeek = getWeeklyRange(now);
      return startDate === currentWeek.startDate && endDate === currentWeek.endDate;
    }
    if (periodMode === "monthly") {
      return (
        anchorDate.getFullYear() === now.getFullYear() &&
        anchorDate.getMonth() === now.getMonth()
      );
    }
    return true;
  }, [periodMode, anchorDate, startDate, endDate]);

  const handlePeriodModeChange = (newMode) => {
    setPeriodMode(newMode);
    const now = new Date();
    setAnchorDate(now);
    let range;
    if (newMode === "all") {
      range = getAllTimeRange(earliestDate);
    } else if (newMode === "daily") {
      range = getDailyRange(now);
    } else if (newMode === "weekly") {
      range = getWeeklyRange(now);
    } else if (newMode === "monthly") {
      range = getMonthlyRange(now);
    } else {
      range = { startDate, endDate };
    }
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    fetchReport({ startDate: range.startDate, endDate: range.endDate, page: 1 });
  };

  const handleNavigatePeriod = (delta) => {
    const nextAnchor = new Date(anchorDate);
    let range;
    if (periodMode === "daily") {
      nextAnchor.setDate(nextAnchor.getDate() + delta);
      range = getDailyRange(nextAnchor);
    } else if (periodMode === "weekly") {
      nextAnchor.setDate(nextAnchor.getDate() + delta * 7);
      range = getWeeklyRange(nextAnchor);
    } else if (periodMode === "monthly") {
      nextAnchor.setMonth(nextAnchor.getMonth() + delta);
      range = getMonthlyRange(nextAnchor);
    }
    if (range) {
      setAnchorDate(nextAnchor);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
      fetchReport({ startDate: range.startDate, endDate: range.endDate, page: 1 });
    }
  };

  const handleJumpToCurrent = () => {
    const now = new Date();
    setAnchorDate(now);
    let range;
    if (periodMode === "daily") {
      range = getDailyRange(now);
    } else if (periodMode === "weekly") {
      range = getWeeklyRange(now);
    } else if (periodMode === "monthly") {
      range = getMonthlyRange(now);
    } else if (periodMode === "all") {
      range = getAllTimeRange(earliestDate);
    }
    if (range) {
      setStartDate(range.startDate);
      setEndDate(range.endDate);
      fetchReport({ startDate: range.startDate, endDate: range.endDate, page: 1 });
    }
  };

  const handleReload = () => {
    setSearchTerm("");
    setTransactionSourceFilter("all");
    setReportFilterType("all");
    setReportCategoryId("");
    setReportProductId("");
    handlePeriodModeChange("all");
  };

  const canShowDetail =
    activeReport === "transaction-history" ||
    activeReport === "void-transactions" ||
    activeReport === "shifts";

  const handleViewDetail = (row) => {
    if (!canShowDetail) return;
    const txId = row?.id || row?.transaction_id || row?.shift_id;
    setSelectedTransaction({
      id: txId || null,
      transaction_number: row?.transaction_no || row?.transaction_number || row?.shift_number || "-",
      ...row,
    });
  };

  const columns = useMemo(() => getReportColumns(activeReport), [activeReport]);

  const NO_ACTION_REPORTS = new Set([
    "recap-products",
    "recap-units",
    "recap-categories",
    "recap-payments",
    "recap-sales-types",
    "sales",
    "profit-loss",
  ]);
  const showActionColumn = !NO_ACTION_REPORTS.has(activeReport);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      return columns.some((col) => {
        const value = row[col.key];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(term);
      });
    });
  }, [rows, columns, searchTerm]);

  // Untuk laporan yang menggunakan grouping rowspan (sales by date & recap by unit).
  // Menghitung kolom group, total per group, dan rowspan untuk setiap baris.
  const groupConfig = useMemo(() => {
    if (activeReport === "sales") {
      return {
        groupKey: "date",
        sumKey: "sub_total",
        totalKey: "total_per_day",
      };
    }
    if (activeReport === "recap-units") {
      return {
        groupKey: "unit",
        sumKey: "sub_total",
        totalKey: "total_pemakaian",
        addRowNumber: true,
      };
    }
    return null;
  }, [activeReport]);

  const dailyTotalMap = useMemo(() => {
    const map = {};
    if (Array.isArray(dailyTotals)) {
      for (const item of dailyTotals) {
        if (!item?.date) continue;
        const d = new Date(item.date);
        if (Number.isNaN(d.getTime())) continue;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        map[key] = Number(item.total_per_day || 0);
      }
    }
    return map;
  }, [dailyTotals]);

  const renderRows = useMemo(() => {
    if (activeReport === "sales") {
      const result = [];
      let i = 0;

      const getDateKey = (r) => {
        if (!r?.date) return "";
        const d = new Date(r.date);
        if (Number.isNaN(d.getTime())) return String(r.date).slice(0, 10);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      };

      const getNotaKey = (r, idx) => {
        if (r?.order_id) return String(r.order_id);
        if (r?.transaction_number) return String(r.transaction_number);
        if (r?.order_number) return String(r.order_number);
        return `${getDateKey(r)}_${r?.total_per_nota}_${r?.receiver}`;
      };

      while (i < filteredRows.length) {
        const currentDateKey = getDateKey(filteredRows[i]);

        let dateEnd = i;
        while (
          dateEnd < filteredRows.length &&
          getDateKey(filteredRows[dateEnd]) === currentDateKey
        ) {
          dateEnd++;
        }
        const dateSpan = dateEnd - i;

        // Ambil total_per_day dari data API (item / dailyTotalMap lengkap selamanya),
        // fallback ke kalkulasi baris halaman jika tidak ditemukan.
        const rowDayTotal = Number(filteredRows[i]?.total_per_day || 0);
        let dayTotal = rowDayTotal > 0 ? rowDayTotal : dailyTotalMap[currentDateKey];
        if (dayTotal === undefined || dayTotal === null || dayTotal === 0) {
          const seenNotas = new Set();
          dayTotal = 0;
          for (let k = i; k < dateEnd; k++) {
            const nKey = getNotaKey(filteredRows[k], k);
            if (!seenNotas.has(nKey)) {
              seenNotas.add(nKey);
              dayTotal += Number(filteredRows[k]?.total_per_nota || 0);
            }
          }
        }

        let k = i;
        while (k < dateEnd) {
          const currentNotaKey = getNotaKey(filteredRows[k], k);
          let notaEnd = k;
          while (
            notaEnd < dateEnd &&
            getNotaKey(filteredRows[notaEnd], notaEnd) === currentNotaKey
          ) {
            notaEnd++;
          }
          const notaSpan = notaEnd - k;

          for (let m = k; m < notaEnd; m++) {
            const isFirstInDate = m === i;
            const isLastInDate = m === dateEnd - 1;
            const isFirstInNota = m === k;

            const augmented = {
              ...filteredRows[m],
              total_per_day: dayTotal,
            };

            const spans = {};
            const skip = {};

            // Merged per DATE (Tanggal & Total / Hari)
            if (isFirstInDate) {
              spans.date = dateSpan;
              spans.total_per_day = dateSpan;
            } else {
              skip.date = true;
              skip.total_per_day = true;
            }

            // Merged per NOTA (Total / Nota, Penerima) — Unit column is unmerged per item
            if (isFirstInNota) {
              spans.total_per_nota = notaSpan;
              spans.receiver = notaSpan;
            } else {
              skip.total_per_nota = true;
              skip.receiver = true;
            }

            result.push({
              row: augmented,
              idx: m,
              spans,
              skip,
              isLastInDate,
            });
          }

          k = notaEnd;
        }

        i = dateEnd;
      }

      return result;
    }

    if (!groupConfig) {
      return filteredRows.map((row, idx) => ({
        row,
        idx,
        spans: {},
        skip: {},
      }));
    }
    const { groupKey, sumKey, totalKey, addRowNumber } = groupConfig;
    const result = [];
    let i = 0;
    let groupNo = 0;
    while (i < filteredRows.length) {
      const groupVal = filteredRows[i]?.[groupKey];
      let j = i;
      let groupSum = 0;
      while (
        j < filteredRows.length &&
        filteredRows[j]?.[groupKey] === groupVal
      ) {
        groupSum += Number(filteredRows[j]?.[sumKey] || 0);
        j += 1;
      }
      const span = j - i;
      groupNo += 1;
      for (let k = i; k < j; k += 1) {
        const isFirst = k === i;
        const augmented = { ...filteredRows[k] };
        if (totalKey) augmented[totalKey] = groupSum;
        if (addRowNumber) augmented.no = groupNo;
        result.push({
          row: augmented,
          idx: k,
          spans: isFirst
            ? {
                [groupKey]: span,
                ...(totalKey ? { [totalKey]: span } : {}),
                ...(addRowNumber ? { no: span } : {}),
              }
            : {},
          skip: !isFirst
            ? {
                [groupKey]: true,
                ...(totalKey ? { [totalKey]: true } : {}),
                ...(addRowNumber ? { no: true } : {}),
              }
            : {},
        });
      }
      i = j;
    }
    return result;
  }, [filteredRows, groupConfig, activeReport]);

  const totalSummary = useMemo(() => {
    if (activeReport === "transaction-history" || activeReport === "sales") {
      return Number(meta?.summary?.total_revenue || 0);
    }
    if (activeReport === "recap-units") {
      return Number(totalUsage || meta?.summary?.total_usage || meta?.summary?.total_sales || 0);
    }
    if (
      activeReport === "recap-products" ||
      activeReport === "recap-categories" ||
      activeReport === "recap-payments" ||
      activeReport === "recap-sales-types" ||
      activeReport === "void-transactions"
    ) {
      return Number(meta?.summary?.total_sales || 0);
    }
    return 0;
  }, [activeReport, meta, totalUsage]);

  const fetchReport = async (override = {}) => {
    const queryStartDate = override.startDate || startDate;
    const queryEndDate = override.endDate || endDate;
    const queryPage = Number(override.page || meta.page);
    const queryLimit = Number(override.limit || meta.limit);

    if (!queryStartDate || !queryEndDate) {
      setError("Tanggal awal dan tanggal akhir wajib diisi.");
      return;
    }

    if (queryEndDate < queryStartDate) {
      setError(
        "Tanggal akhir harus lebih besar atau sama dengan tanggal awal.",
      );
      return;
    }

    setLoading(true);
    setError(null);

    const params = {
      start_date: queryStartDate,
      end_date: queryEndDate,
      page: queryPage,
      limit: queryLimit,
    };

    const activeFilterType = override.reportFilterType !== undefined ? override.reportFilterType : reportFilterType;
    const activeCategoryId = override.reportCategoryId !== undefined ? override.reportCategoryId : reportCategoryId;
    const activeProductId = override.reportProductId !== undefined ? override.reportProductId : reportProductId;

    if (activeReport === "profit-loss") {
      if (activeFilterType === "category" && activeCategoryId) {
        params.category_id = activeCategoryId;
      } else if (activeFilterType === "product" && activeProductId) {
        params.product_id = activeProductId;
      }
    }

    try {
      let response;

      if (activeReport === "transaction-history") {
        const sourceVal = override.transactionSourceFilter !== undefined ? override.transactionSourceFilter : transactionSourceFilter;
        if (sourceVal && sourceVal !== "all") {
          params.source = sourceVal;
        }
        response = await reportService.getTransactionHistory(params);
        setRows(response?.data || []);
      } else if (activeReport === "shifts") {
        response = await reportService.getShiftHistory(params);
        setRows(response?.data || []);
      } else if (activeReport === "sales") {
        const stVal = override.salesTypeFilter !== undefined ? override.salesTypeFilter : salesTypeFilter;
        if (stVal && stVal !== "all") {
          params.sales_type_id = stVal;
        }
        response = await reportService.getSalesReport(params);
        setRows(response?.data?.items || []);
        setDailyTotals(response?.data?.daily_totals || []);
      } else if (activeReport === "recap-products") {
        response = await reportService.getProductRecap(params);
        setRows(response?.data || []);
      } else if (activeReport === "recap-units") {
        response = await reportService.getUnitRecap(params);
        const unitItems = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.items)
          ? response.data.items
          : [];
        setRows(unitItems);
        setTotalUsage(Number(response?.data?.total_usage || response?.meta?.summary?.total_usage || 0));
      } else if (activeReport === "recap-categories") {
        response = await reportService.getCategoryRecap(params);
        setRows(response?.data || []);
      } else if (activeReport === "recap-payments") {
        response = await reportService.getPaymentRecap(params);
        setRows(response?.data || []);
      } else if (activeReport === "recap-sales-types") {
        response = await reportService.getSalesTypeRecap(params);
        setRows(response?.data || []);
      } else if (activeReport === "void-transactions") {
        response = await reportService.getVoidTransactions(params);
        setRows(response?.data || []);
      } else if (activeReport === "profit-loss") {
        response = await reportService.getProfitLoss(params);
        setProfitLossData(response?.data || null);
        setRows([]);
      } else if (activeReport === "profit-sales") {
        response = await reportService.getProfitSalesReport(params);
        setProfitSalesData(response?.data?.data || response?.data || null);
        setRows([]);
      }

      const responseMeta = response?.meta || {};
      setMeta({
        page: Number(responseMeta.page || queryPage),
        limit: Number(responseMeta.limit || queryLimit),
        total: Number(responseMeta.total || 0),
        summary: responseMeta.summary || {},
      });
    } catch (err) {
      const message = err?.message || "Gagal memuat data laporan";
      setError(message);
      showError(message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initDate = async () => {
      try {
        const res = await reportService.getActivePeriod();
        if (res?.data?.start_date) {
          setEarliestDate(res.data.start_date);
          setStartDate(res.data.start_date);
        }
      } catch (err) {
        console.error("Failed to fetch active period", err);
      } finally {
        setHasInitDate(true);
      }
    };
    initDate();
    salesTypeService
      .list({ limit: 100 })
      .then((res) => setSalesTypes(res?.data || []))
      .catch((err) =>
        console.error("Failed to load sales types for report filter", err),
      );
  }, []);

  useEffect(() => {
    if (showDateFilter && activeReport === "profit-loss") {
      if (categories.length === 0) {
        masterService.categories.list({ status: "ACTIVE" })
          .then((res) => setCategories(res?.data || []))
          .catch(console.error);
      }
      if (products.length === 0) {
        masterService.products.list({ status: "ACTIVE" })
          .then((res) => setProducts(res?.data || []))
          .catch(console.error);
      }
    }
  }, [showDateFilter, activeReport]);

  useEffect(() => {
    if (hasInitDate) {
      fetchReport({ page: 1, limit: meta.limit });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport, hasInitDate, transactionSourceFilter, salesTypeFilter]);

  const handleApplyFilter = () => {
    fetchReport({
      startDate,
      endDate,
      page: 1,
      limit: meta.limit,
    });
  };

  const handleResetFilter = async () => {
    let resetStartDate = earliestDate || toInputDate(new Date());
    const resetEndDate = toInputDate(new Date());
    
    try {
      const res = await reportService.getActivePeriod();
      if (res?.data?.start_date) {
        resetStartDate = res.data.start_date;
        setEarliestDate(res.data.start_date);
      }
    } catch (err) {
      console.error(err);
    }

    setPeriodMode("all");
    setStartDate(resetStartDate);
    setEndDate(resetEndDate);
    setReportFilterType("all");
    setReportCategoryId("");
    setReportProductId("");
    fetchReport({
      startDate: resetStartDate,
      endDate: resetEndDate,
      page: 1,
      limit: meta.limit,
      reportFilterType: "all",
      reportCategoryId: "",
      reportProductId: "",
    });
  };

  const maxPage = Math.max(
    1,
    Math.ceil((meta.total || 0) / (meta.limit || DEFAULT_LIMIT)),
  );
  const startEntry = meta.total === 0 ? 0 : (meta.page - 1) * meta.limit + 1;
  const endEntry = Math.min(meta.page * meta.limit, meta.total);

  // === Summary cards (computed dari data API, unik per tab) ===
  const summaryCards = useMemo(() => {
    const periodDays = (() => {
      if (!startDate || !endDate) return 0;
      const s = new Date(startDate);
      const e = new Date(endDate);
      return Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1);
    })();

    const totalRows = meta.total || filteredRows.length;

    // Helper: cari item dengan nilai tertinggi pada key tertentu
    const findTop = (key, labelKey) => {
      if (!filteredRows.length) return null;
      let top = filteredRows[0];
      let topVal = Number(top?.[key] || 0);
      for (const r of filteredRows) {
        const v = Number(r?.[key] || 0);
        if (v > topVal) {
          top = r;
          topVal = v;
        }
      }
      return { name: top?.[labelKey] || "-", value: topVal };
    };

    if (activeReport === "transaction-history") {
      const avg = totalRows > 0 ? totalSummary / totalRows : 0;
      return [
        {
          key: "tx-count",
          label: "Total Transaksi",
          value: formatNumber(totalRows),
          hint: "Nota tercatat di periode ini",
          icon: History,
          color: "teal",
        },
        {
          key: "tx-revenue",
          label: "Pendapatan Bersih",
          value: formatCurrency(totalSummary),
          hint: "Akumulasi grand total setelah diskon",
          icon: Wallet,
          color: "emerald",
          accent: true,
        },
        {
          key: "tx-avg",
          label: "Rata-rata per Nota",
          value: formatCurrency(avg),
          hint: `Dari ${formatNumber(totalRows)} transaksi`,
          icon: TrendingUp,
          color: "violet",
        },
        {
          key: "tx-period",
          label: "Periode Aktif",
          value: `${periodDays} hari`,
          hint: `${formatDate(startDate)} \u2013 ${formatDate(endDate)}`,
          icon: CalendarRange,
          color: "amber",
        },
      ];
    }

    if (activeReport === "shifts") {
      const closedShifts = Number(meta?.summary?.closed_shifts || 0);
      const totalCashDifference = Number(meta?.summary?.total_cash_difference || 0);
      const totalSales = Number(meta?.summary?.total_sales || 0);
      return [
        {
          key: "sh-count",
          label: "Total Shift",
          value: formatNumber(totalRows),
          hint: "Shift tercatat di periode ini",
          icon: Clock,
          color: "teal",
        },
        {
          key: "sh-closed",
          label: "Shift Selesai",
          value: formatNumber(closedShifts),
          hint: "Status shift CLOSED",
          icon: CheckCircle,
          color: "emerald",
        },
        {
          key: "sh-diff",
          label: "Total Selisih Kas",
          value: formatCurrency(totalCashDifference),
          hint: "Akumulasi selisih kas fisik",
          icon: Wallet,
          color: totalCashDifference < 0 ? "rose" : "indigo",
          accent: true,
        },
        {
          key: "sh-sales",
          label: "Total Penjualan Shift",
          value: formatCurrency(totalSales),
          hint: "Akumulasi penjualan bersih shift",
          icon: TrendingUp,
          color: "sky",
        },
      ];
    }

    if (activeReport === "sales") {
      const totalQty = Number(meta?.summary?.total_qty || 0);
      const activeDays = dailyTotals.length || 0;
      const avgPerDay = activeDays > 0 ? totalSummary / activeDays : 0;
      return [
        {
          key: "s-items",
          label: "Total Item Terjual",
          value: formatNumber(totalQty),
          hint: `${formatNumber(totalRows)} baris penjualan`,
          icon: Package,
          color: "indigo",
        },
        {
          key: "s-revenue",
          label: "Penjualan Kotor (Subtotal)",
          value: formatCurrency(totalSummary),
          hint: "Akumulasi subtotal sebelum diskon",
          icon: Wallet,
          color: "emerald",
          accent: true,
        },
        {
          key: "s-days",
          label: "Hari Aktif",
          value: formatNumber(activeDays),
          hint: `Dari ${periodDays} hari periode`,
          icon: CalendarRange,
          color: "sky",
        },
        {
          key: "s-avg",
          label: "Rata-rata per Hari",
          value: formatCurrency(avgPerDay),
          hint: "Rata-rata penjualan harian",
          icon: TrendingUp,
          color: "amber",
        },
      ];
    }

    if (activeReport === "recap-products") {
      const totalQty = Number(meta?.summary?.total_qty || 0);
      const top = findTop("total_sales", "product_name");
      return [
        {
          key: "p-count",
          label: "Jumlah Produk",
          value: formatNumber(totalRows),
          hint: "Produk terjual di periode ini",
          icon: Package,
          color: "emerald",
        },
        {
          key: "p-qty",
          label: "Total Qty Terjual",
          value: formatNumber(totalQty),
          hint: "Akumulasi seluruh unit",
          icon: Hash,
          color: "sky",
        },
        {
          key: "p-revenue",
          label: "Subtotal Penjualan Produk",
          value: formatCurrency(totalSummary),
          hint: "Akumulasi subtotal sebelum diskon",
          icon: Wallet,
          color: "teal",
          accent: true,
        },
        {
          key: "p-top",
          label: "Produk Terlaris",
          value: top ? top.name : "-",
          hint: top ? formatCurrency(top.value) : "Belum ada data",
          icon: Sparkles,
          color: "amber",
        },
      ];
    }

    if (activeReport === "recap-units") {
      const safeRows = Array.isArray(filteredRows) ? filteredRows : [];
      const distinctUnits = new Set(safeRows.map((r) => r.unit || r.unit_name).filter(Boolean));
      const totalUnits = distinctUnits.size || Number(meta?.total || 0);
      const totalSales = safeRows.reduce((sum, r) => sum + Number(r.sub_total || r.total_sales || 0), 0);
      const totalQty = safeRows.reduce((sum, r) => sum + Number(r.quantity || r.total_items || 0), 0);
      const top = findTop("sub_total", "unit") || findTop("total_sales", "unit_name");
      return [
        {
          key: "u-count",
          label: "Jumlah Unit",
          value: `${formatNumber(totalUnits)} Unit`,
          hint: "Unit terdata",
          icon: Truck,
          color: "amber",
        },
        {
          key: "u-qty",
          label: "Total Suku Cadang",
          value: `${formatNumber(totalQty)} Item`,
          hint: "Akumulasi suku cadang",
          icon: Package,
          color: "sky",
        },
        {
          key: "u-sales",
          label: "Total Nilai",
          value: formatCurrency(totalSales),
          hint: "Total nilai suku cadang",
          icon: Wallet,
          color: "emerald",
          accent: true,
        },
        {
          key: "u-top",
          label: "Unit Terbanyak",
          value: top ? top.name : "-",
          hint: top ? formatCurrency(top.value) : "Belum ada data",
          icon: Sparkles,
          color: "violet",
        },
      ];
    }

    if (activeReport === "recap-categories") {
      const txCount = Number(meta?.summary?.transaction_count || 0);
      const top = findTop("total_sales", "name");
      return [
        {
          key: "c-count",
          label: "Jumlah Kategori",
          value: formatNumber(totalRows),
          hint: "Kategori aktif bertransaksi",
          icon: Layers,
          color: "sky",
        },
        {
          key: "c-tx",
          label: "Total Transaksi",
          value: formatNumber(txCount),
          hint: "Lintas kategori produk",
          icon: History,
          color: "violet",
        },
        {
          key: "c-revenue",
          label: "Subtotal Penjualan Kategori",
          value: formatCurrency(totalSummary),
          hint: "Akumulasi subtotal sebelum diskon",
          icon: Wallet,
          color: "emerald",
          accent: true,
        },
        {
          key: "c-top",
          label: "Kategori Terlaris",
          value: top ? top.name : "-",
          hint: top ? formatCurrency(top.value) : "Belum ada data",
          icon: Sparkles,
          color: "amber",
        },
      ];
    }

    if (activeReport === "recap-payments") {
      const txCount = Number(meta?.summary?.transaction_count || 0);
      const top = findTop("total_sales", "payment_method");
      return [
        {
          key: "pay-count",
          label: "Metode Aktif",
          value: formatNumber(totalRows),
          hint: "Jenis pembayaran dipakai",
          icon: CreditCard,
          color: "violet",
        },
        {
          key: "pay-tx",
          label: "Total Transaksi",
          value: formatNumber(txCount),
          hint: "Lintas metode pembayaran",
          icon: History,
          color: "sky",
        },
        {
          key: "pay-revenue",
          label: "Total Pembayaran (Bersih)",
          value: formatCurrency(totalSummary),
          hint: "Akumulasi pembayaran bersih diterima",
          icon: Wallet,
          color: "emerald",
          accent: true,
        },
        {
          key: "pay-top",
          label: "Metode Terpopuler",
          value: top ? top.name : "-",
          hint: top ? formatCurrency(top.value) : "Belum ada data",
          icon: Sparkles,
          color: "amber",
        },
      ];
    }

    if (activeReport === "recap-sales-types") {
      const txCount = Number(meta?.summary?.transaction_count || 0);
      const top = findTop("total_sales", "sales_type");
      const topRatio = filteredRows.reduce(
        (acc, r) => Math.max(acc, Number(r?.transaction_ratio || 0)),
        0,
      );
      return [
        {
          key: "st-count",
          label: "Tipe Penjualan",
          value: formatNumber(totalRows),
          hint: "Tipe yang aktif dipakai",
          icon: Tag,
          color: "pink",
        },
        {
          key: "st-tx",
          label: "Total Transaksi",
          value: formatNumber(txCount),
          hint: "Lintas tipe penjualan",
          icon: History,
          color: "sky",
        },
        {
          key: "st-revenue",
          label: "Pendapatan Bersih",
          value: formatCurrency(totalSummary),
          hint: "Akumulasi grand total per tipe",
          icon: Wallet,
          color: "emerald",
          accent: true,
        },
        {
          key: "st-top",
          label: "Tipe Dominan",
          value: top ? top.name : "-",
          hint: top
            ? `${formatNumber(topRatio)}% dari total`
            : "Belum ada data",
          icon: TrendingUp,
          color: "amber",
        },
      ];
    }

    if (activeReport === "void-transactions") {
      const avg = totalRows > 0 ? totalSummary / totalRows : 0;
      const dailyAvg = periodDays > 0 ? totalRows / periodDays : 0;
      return [
        {
          key: "v-count",
          label: "Jumlah Void",
          value: formatNumber(totalRows),
          hint: "Transaksi dibatalkan",
          icon: XCircle,
          color: "rose",
          accent: true,
        },
        {
          key: "v-nominal",
          label: "Total Nominal Void",
          value: formatCurrency(totalSummary),
          hint: "Nilai yang dibatalkan",
          icon: Wallet,
          color: "amber",
        },
        {
          key: "v-avg",
          label: "Rata-rata per Void",
          value: formatCurrency(avg),
          hint: `Dari ${formatNumber(totalRows)} transaksi`,
          icon: TrendingUp,
          color: "violet",
        },
        {
          key: "v-frequency",
          label: "Frekuensi Harian",
          value: `${dailyAvg.toFixed(1)} / hari`,
          hint: `Selama ${periodDays} hari periode`,
          icon: CalendarRange,
          color: "sky",
        },
      ];
    }

    if (activeReport === "profit-loss") {
      const d = profitLossData || {
        pendapatan: 0, diskon: 0, pajak: 0, pendapatan_bersih: 0,
        hpp: 0, biaya_operasional: 0, total_biaya: 0, laba_bersih: 0
      };
      return [
        { key: "pl-rev", label: "Pendapatan Kotor", value: formatCurrency(d.pendapatan), hint: "Subtotal sebelum diskon", icon: TrendingUp, color: "emerald" },
        { key: "pl-disc", label: "Total Diskon", value: formatCurrency(d.diskon), hint: "Potongan harga penjualan", icon: Tag, color: "rose" },
        { key: "pl-tax", label: "Total Pajak", value: formatCurrency(d.pajak), hint: "Pajak transaksi", icon: Hash, color: "indigo" },
        { key: "pl-net", label: "Pendapatan Bersih", value: formatCurrency(d.pendapatan_bersih), hint: "Pendapatan setelah diskon", icon: FileBarChart2, color: "teal" },
        
        { key: "pl-hpp", label: "Total HPP", value: formatCurrency(d.hpp), hint: "Harga Pokok Penjualan", icon: Package, color: "blue" },
        { key: "pl-op", label: "Biaya Operasional", value: formatCurrency(d.biaya_operasional), hint: "Pengeluaran kas operasional", icon: Database, color: "amber" },
        { key: "pl-cost", label: "Total Biaya", value: formatCurrency(d.total_biaya), hint: "HPP + Biaya Operasional", icon: Wallet, color: "amber" },
        { key: "pl-profit", label: "Laba Bersih", value: formatCurrency(d.laba_bersih), hint: "Pendapatan Bersih - Total Biaya", icon: TrendingUp, color: "emerald", accent: true },
      ];
    }

    // fallback
    return [];
  }, [
    activeReport,
    filteredRows,
    meta.total,
    totalSummary,
    dailyTotals,
    startDate,
    endDate,
    profitLossData,
  ]);

  const activeMeta = REPORT_META[activeReport] || REPORTS[0];
  const ActiveIcon = activeMeta.icon || FileBarChart2;

  const [isExporting, setIsExporting] = useState(false);

  const handleExportExcel = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);

      if (activeReport === "profit-sales") {
        if (!profitSalesData?.transactions?.length) {
          showInfo("Tidak ada data transaksi untuk diekspor pada periode ini");
          return;
        }
        await exportProfitSalesToExcel({
          storeInfo,
          reportData: profitSalesData,
          startDate: formatPeriodLabel(periodMode, anchorDate, startDate, endDate),
          endDate: formatPeriodLabel(periodMode, anchorDate, startDate, endDate),
        });
        showSuccess("Laporan Profit Penjualan berhasil diexport ke Excel");
        return;
      }

      const params = {
        start_date: startDate,
        end_date: endDate,
        startDate,
        endDate,
        page: 1,
        limit: 50000,
      };
      if (activeReport === "profit-loss") {
        if (reportFilterType === "category" && reportCategoryId) {
          params.category_id = reportCategoryId;
        } else if (reportFilterType === "product" && reportProductId) {
          params.product_id = reportProductId;
        }
      }
      if (
        activeReport === "transaction-history" &&
        transactionSourceFilter &&
        transactionSourceFilter !== "all"
      ) {
        params.source = transactionSourceFilter;
      }
      if (
        activeReport === "sales" &&
        salesTypeFilter &&
        salesTypeFilter !== "all"
      ) {
        params.sales_type_id = salesTypeFilter;
      }

      let exportRows = [];
      let exportDailyTotals = [];
      let exportProfitLoss = null;

      if (activeReport === "profit-loss") {
        const res = await reportService.getProfitLoss(params);
        exportProfitLoss = res?.data || profitLossData || {};
      } else {
        let res = null;
        if (activeReport === "transaction-history") {
          res = await reportService.getTransactionHistory(params);
        } else if (activeReport === "shifts") {
          res = await reportService.getShiftHistory(params);
        } else if (activeReport === "sales") {
          res = await reportService.getSalesReport(params);
          exportDailyTotals =
            res?.data?.daily_totals ||
            res?.daily_totals ||
            dailyTotals ||
            [];
        } else if (activeReport === "recap-products") {
          res = await reportService.getProductRecap(params);
        } else if (activeReport === "recap-units") {
          res = await reportService.getUnitRecap(params);
        } else if (activeReport === "recap-categories") {
          res = await reportService.getCategoryRecap(params);
        } else if (activeReport === "recap-payments") {
          res = await reportService.getPaymentRecap(params);
        } else if (activeReport === "recap-sales-types") {
          res = await reportService.getSalesTypeRecap(params);
        } else if (activeReport === "void-transactions") {
          res = await reportService.getVoidTransactions(params);
        }

        let rawData = [];
        if (activeReport === "sales") {
          rawData = Array.isArray(res?.data?.items)
            ? res.data.items
            : Array.isArray(res?.data)
            ? res.data
            : [];
        } else if (activeReport === "recap-units") {
          rawData = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.data?.items)
            ? res.data.items
            : [];
        } else {
          rawData = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.data?.items)
            ? res.data.items
            : Array.isArray(res?.items)
            ? res.items
            : [];
        }

        exportRows = rawData;

        // Apply same search filter if active
        if (searchTerm && searchTerm.trim()) {
          const q = searchTerm.toLowerCase().trim();
          exportRows = exportRows.filter((row) =>
            columns.some((col) => {
              const val = row[col.key];
              return (
                val !== null &&
                val !== undefined &&
                String(val).toLowerCase().includes(q)
              );
            })
          );
        }
      }

      if (
        activeReport !== "profit-loss" &&
        (!exportRows || exportRows.length === 0)
      ) {
        showInfo("Tidak ada data untuk diekspor pada periode ini");
        return;
      }

      await exportReportToExcel({
        activeReport,
        reportLabel: activeMeta.label || "Laporan",
        rows: exportRows,
        profitLossData: exportProfitLoss,
        dailyTotals: exportDailyTotals,
        startDate,
        endDate,
      });

      showSuccess("Laporan berhasil diekspor ke Excel!");
    } catch (err) {
      console.error("Export Excel failed:", err);
      showError(err?.message || "Gagal mengekspor data ke Excel");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      if (activeReport === "profit-sales") {
        if (!profitSalesData?.transactions?.length) {
          showInfo("Tidak ada data transaksi untuk diekspor ke PDF pada periode ini");
          return;
        }
        await exportProfitSalesToPdf({
          storeInfo,
          reportData: profitSalesData,
          startDate: formatPeriodLabel(periodMode, anchorDate, startDate, endDate),
          endDate: formatPeriodLabel(periodMode, anchorDate, startDate, endDate),
        });
        showSuccess("Laporan Profit Penjualan berhasil diexport ke PDF");
      }
    } catch (err) {
      console.error("Export PDF failed:", err);
      showError(err?.message || "Gagal mengekspor data ke PDF");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">
      <div
        className={`fixed inset-y-0 left-0 z-50 transition-all duration-300 pointer-events-none md:pointer-events-auto ${isSidebarCollapsed ? "w-16" : "w-48"}`}>
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() =>
            setIsSidebarCollapsed((prev) => {
              const newState = !prev;
              localStorage.setItem(
                "sidebarCollapsed",
                JSON.stringify(newState),
              );
              return newState;
            })
          }
          activePage="laporan"
        />
      </div>

      <div
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header />

        <main className="flex-1 p-4 lg:p-6 overflow-x-hidden overflow-y-auto">
          {/* === Tab Card (Laporan Type Tabs) === */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 p-3 mb-4 shadow-sm">
            <div className="overflow-x-auto -mx-1 px-1">
              <div className="flex items-center min-w-max gap-2">
                {visibleReports.map((report) => {
                  const Icon = report.icon || FileBarChart2;
                  const isActive = activeReport === report.key;
                  return (
                    <button
                      key={report.key}
                      onClick={() => {
                        setActiveReport(report.key);
                        setMeta((prev) => ({ ...prev, page: 1 }));
                      }}
                      className={`group relative inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                        isActive
                          ? "bg-gradient-to-br from-teal-600 to-emerald-600 text-white shadow-md shadow-teal-600/30 ring-1 ring-teal-500"
                          : "bg-slate-50 text-slate-600 ring-1 ring-transparent hover:text-teal-700 hover:bg-teal-50 hover:ring-teal-200"
                      }`}>
                      <span
                        className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${
                          isActive
                            ? "bg-white/20"
                            : "bg-white text-teal-600 group-hover:bg-teal-100"
                        }`}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      {report.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* === Mobile Smart Period Selector & Date Navigator (Visible on Mobile Only) === */}
          <div className="block md:hidden bg-white rounded-2xl ring-1 ring-slate-200/70 p-3 sm:p-4 mb-4 shadow-sm flex flex-col justify-between gap-3">
            {/* Segmented Period Tabs: Semua | Harian | Mingguan | Bulanan | Kustom */}
            <div className="inline-flex p-1 bg-slate-100/90 rounded-xl w-full shadow-xs overflow-x-auto">
              {[
                { id: "all", label: "Semua" },
                { id: "daily", label: "Harian" },
                { id: "weekly", label: "Mingguan" },
                { id: "monthly", label: "Bulanan" },
                { id: "custom", label: "Kustom" },
              ].map((p) => {
                const isActive = periodMode === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handlePeriodModeChange(p.id)}
                    className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer border-0 whitespace-nowrap ${
                      isActive
                        ? "bg-teal-600 text-white shadow-xs"
                        : "bg-transparent text-slate-500 hover:text-slate-900"
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            {/* Date Navigator / Custom Range */}
            {periodMode === "custom" ? (
              /* Custom Date Range Picker */
              <div className="flex items-center gap-2 w-full flex-wrap">
                <div className="flex items-center gap-1.5 text-xs text-slate-600 w-full">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg text-xs font-medium text-slate-800"
                  />
                  <span>s/d</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg text-xs font-medium text-slate-800"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleApplyFilter}
                  className="w-full py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold transition-colors border-0 cursor-pointer shrink-0"
                >
                  Terapkan
                </button>
              </div>
            ) : periodMode !== "all" ? (
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="inline-flex items-center gap-1 bg-slate-50 ring-1 ring-slate-200/80 rounded-xl p-1 w-full justify-between shadow-xs">
                  <button
                    type="button"
                    onClick={() => handleNavigatePeriod(-1)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-600 hover:bg-white hover:text-slate-900 transition-all border-0 cursor-pointer"
                    title="Periode Sebelumnya"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <span className="px-3 text-xs font-bold text-slate-800 whitespace-nowrap text-center flex-1">
                    {formatPeriodLabel(periodMode, anchorDate, startDate, endDate)}
                  </span>

                  <button
                    type="button"
                    onClick={() => handleNavigatePeriod(1)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-600 hover:bg-white hover:text-slate-900 transition-all border-0 cursor-pointer"
                    title="Periode Berikutnya"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Quick jump to Today / This Week / This Month if navigated away */}
                {!isCurrentPeriod && (
                  <button
                    type="button"
                    onClick={handleJumpToCurrent}
                    className="px-2.5 py-1.5 rounded-xl bg-teal-100/70 text-teal-800 hover:bg-teal-200/80 text-xs font-bold transition-colors border-0 cursor-pointer shrink-0"
                  >
                    {periodMode === "daily" ? "Hari Ini" : periodMode === "weekly" ? "Minggu Ini" : "Bulan Ini"}
                  </button>
                )}
              </div>
            ) : null}
          </div>

          {/* === Summary Cards (Directly Below Period Selector) === */}
          {isAdmin && summaryCards.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              {summaryCards.map(({ key, ...cardProps }) => (
                <SummaryCard key={key} {...cardProps} loading={loading} />
              ))}
            </div>
          )}

          {/* === Table Card === */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 overflow-hidden shadow-sm">
            {/* Header: title + desktop source filter + search + reload */}
            <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2.5 sm:gap-3 text-slate-700 min-w-0 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 inline-flex items-center justify-center flex-shrink-0">
                    <ActiveIcon className="w-4 h-4" />
                  </span>
                  <span className="text-sm font-bold truncate">
                    {activeMeta.label}
                  </span>
                  {activeReport !== "profit-loss" && (
                    <span className="text-xs text-slate-400 whitespace-nowrap">
                      · {formatNumber(activeReport === "profit-sales" ? (profitSalesData?.transactions?.length || 0) : (meta.total || filteredRows.length))} baris
                    </span>
                  )}
                </div>

                {/* Desktop Inline Source Sub-Filter for Transaction History */}
                {activeReport === "transaction-history" && (
                  <div className="hidden lg:inline-flex items-center p-0.5 bg-slate-100 rounded-lg ml-2">
                    {[
                      { id: "all", label: "Semua" },
                      { id: "POS", label: "Kasir POS" },
                      { id: "ECATALOG_PUBLIC", label: "E-Katalog" },
                    ].map((tab) => {
                      const active = (transactionSourceFilter || "all") === tab.id;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => setTransactionSourceFilter(tab.id)}
                          className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all border-0 cursor-pointer ${
                            active
                              ? "bg-white text-slate-900 shadow-xs"
                              : "bg-transparent text-slate-500 hover:text-slate-900"
                          }`}
                        >
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Inline Sales Type Filter for Laporan Bulanan (Sales) */}
                {activeReport === "sales" && salesTypes.length > 0 && (
                  <div className="inline-flex items-center gap-1.5 ml-0 sm:ml-2">
                    <select
                      value={salesTypeFilter}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSalesTypeFilter(val);
                        fetchReport({ salesTypeFilter: val, page: 1 });
                      }}
                      className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200/80 ring-1 ring-slate-200 border-0 rounded-lg text-xs font-bold text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors"
                      title="Filter berdasarkan Tipe Penjualan"
                    >
                      <option value="all">Semua Tipe Penjualan</option>
                      {salesTypes.map((st) => (
                        <option key={st.id} value={st.id}>
                          {st.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Right: Search + Actions */}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                {activeReport !== "profit-loss" && (
                  <div className="relative flex-1 sm:w-60 md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={activeReport === "profit-sales" ? "Cari nomor transaksi, pelanggan, atau produk..." : "Cari transaksi / data..."}
                      className="w-full pl-9 pr-3 py-1.5 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                    />
                  </div>
                )}

                {/* Desktop & Tablet Filter Popover Trigger Button (Semua, Harian, Mingguan, Bulanan, Kustom) */}
                <div className="relative hidden md:block" ref={desktopFilterRef}>
                  <button
                    type="button"
                    onClick={() => setIsDesktopFilterOpen((prev) => !prev)}
                    className={`p-2 rounded-lg ring-1 transition-all flex items-center gap-1.5 cursor-pointer shadow-xs ${
                      isDesktopFilterOpen || periodMode !== "all"
                        ? "bg-teal-50 text-teal-700 ring-teal-400 font-bold"
                        : "bg-white hover:bg-slate-50 text-slate-600 ring-slate-200"
                    }`}
                    title="Filter Periode & Tanggal">
                    <Filter className="w-4 h-4" />
                    {periodMode !== "all" && (
                      <span className="hidden xl:inline text-xs font-semibold max-w-[130px] truncate">
                        {periodMode === "custom" ? "Kustom" : formatPeriodLabel(periodMode, anchorDate, startDate, endDate)}
                      </span>
                    )}
                    {periodMode !== "all" && (
                      <span className="w-2 h-2 rounded-full bg-teal-500 xl:hidden"></span>
                    )}
                  </button>

                  {/* Dropdown Popover */}
                  {isDesktopFilterOpen && (
                    <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-xl ring-1 ring-slate-200/80 p-4 z-50 animate-in fade-in zoom-in-95 duration-150">
                      {/* Popover Header */}
                      <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
                            <Filter className="w-3.5 h-3.5" />
                          </span>
                          <div>
                            <h4 className="text-xs font-bold text-slate-900 leading-tight">Filter Periode Laporan</h4>
                            <p className="text-[10px] text-slate-400">Pilih rentang waktu data</p>
                          </div>
                        </div>
                        {periodMode !== "all" && (
                          <button
                            type="button"
                            onClick={() => {
                              handlePeriodModeChange("all");
                            }}
                            className="text-[11px] font-bold text-teal-600 hover:text-teal-700 cursor-pointer">
                            Reset
                          </button>
                        )}
                      </div>

                      {/* Segmented Period Tabs */}
                      <div className="grid grid-cols-5 p-1 bg-slate-100 rounded-xl mb-3 gap-0.5">
                        {[
                          { id: "all", label: "Semua" },
                          { id: "daily", label: "Harian" },
                          { id: "weekly", label: "Mingguan" },
                          { id: "monthly", label: "Bulanan" },
                          { id: "custom", label: "Kustom" },
                        ].map((p) => {
                          const isActive = periodMode === p.id;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => handlePeriodModeChange(p.id)}
                              className={`py-1.5 px-1 rounded-lg text-xs font-bold text-center transition-all cursor-pointer border-0 whitespace-nowrap ${
                                isActive
                                  ? "bg-teal-600 text-white shadow-xs"
                                  : "bg-transparent text-slate-500 hover:text-slate-900"
                              }`}>
                              {p.label}
                            </button>
                          );
                        })}
                      </div>

                      {/* Date Navigator / Custom Range in Popover */}
                      {periodMode === "custom" ? (
                        <div className="space-y-2.5 pt-1">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Dari Tanggal</label>
                              <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full px-2.5 py-1.5 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg text-xs font-medium text-slate-800"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Sampai Tanggal</label>
                              <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full px-2.5 py-1.5 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg text-xs font-medium text-slate-800"
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              handleApplyFilter();
                              setIsDesktopFilterOpen(false);
                            }}
                            className="w-full py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold transition-colors cursor-pointer shadow-xs">
                            Terapkan Filter
                          </button>
                        </div>
                      ) : periodMode !== "all" ? (
                        <div className="space-y-2.5 pt-1">
                          <div className="flex items-center justify-between bg-slate-50 ring-1 ring-slate-200/80 rounded-xl p-1 shadow-xs">
                            <button
                              type="button"
                              onClick={() => handleNavigatePeriod(-1)}
                              className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-600 hover:bg-white hover:text-slate-900 transition-all border-0 cursor-pointer"
                              title="Periode Sebelumnya">
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="px-2 text-xs font-bold text-slate-800 whitespace-nowrap text-center">
                              {formatPeriodLabel(periodMode, anchorDate, startDate, endDate)}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleNavigatePeriod(1)}
                              className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-600 hover:bg-white hover:text-slate-900 transition-all border-0 cursor-pointer"
                              title="Periode Berikutnya">
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>

                          {!isCurrentPeriod && (
                            <button
                              type="button"
                              onClick={handleJumpToCurrent}
                              className="w-full py-1.5 rounded-lg bg-teal-50 text-teal-800 hover:bg-teal-100 text-xs font-bold transition-colors cursor-pointer text-center">
                              Lompat ke {periodMode === "daily" ? "Hari Ini" : periodMode === "weekly" ? "Minggu Ini" : "Bulan Ini"}
                            </button>
                          )}
                        </div>
                      ) : (
                        <p className="text-center text-xs text-slate-400 py-2">
                          Menampilkan seluruh data tanpa batasan waktu
                        </p>
                      )}

                      {/* Special Filter for Profit Loss if on Profit Loss tab */}
                      {activeReport === "profit-loss" && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5">Kategori / Produk</label>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={handleResetFilter}
                              className={`flex-1 py-1 rounded-lg text-xs font-medium transition-all ${
                                reportFilterType === "all"
                                  ? "bg-teal-600 text-white"
                                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              }`}>
                              Semua
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setReportFilterType("category");
                                setReportProductId("");
                              }}
                              className={`flex-1 py-1 rounded-lg text-xs font-medium transition-all ${
                                reportFilterType === "category"
                                  ? "bg-teal-600 text-white"
                                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              }`}>
                              Kategori
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setReportFilterType("product");
                                setReportCategoryId("");
                              }}
                              className={`flex-1 py-1 rounded-lg text-xs font-medium transition-all ${
                                reportFilterType === "product"
                                  ? "bg-teal-600 text-white"
                                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              }`}>
                              Produk
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Special Filter for Sales Type if on Laporan Bulanan (Sales) */}
                      {activeReport === "sales" && salesTypes.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5">Tipe Penjualan</label>
                          <select
                            value={salesTypeFilter}
                            onChange={(e) => {
                              const val = e.target.value;
                              setSalesTypeFilter(val);
                              fetchReport({ salesTypeFilter: val, page: 1 });
                            }}
                            className="w-full px-2.5 py-1.5 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500"
                          >
                            <option value="all">Semua Tipe Penjualan</option>
                            {salesTypes.map((st) => (
                              <option key={st.id} value={st.id}>
                                {st.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Export Excel Button */}
                <button
                  type="button"
                  onClick={handleExportExcel}
                  disabled={isExporting}
                  className="p-2 bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-300 rounded-lg text-emerald-800 transition-colors flex items-center justify-center shrink-0 disabled:opacity-50 cursor-pointer shadow-xs"
                  title="Export Excel (.xlsx)">
                  {isExporting ? (
                    <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <ExcelIcon className="w-4 h-4" />
                  )}
                </button>

                <button
                  onClick={handleReload}
                  className="p-2 bg-white ring-1 ring-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors shrink-0"
                  title="Muat ulang (Reset filter)">
                  <RotateCcw className="w-4 h-4" />
                </button>
                {activeReport === "profit-loss" && (
                  <button
                    onClick={() => setShowDateFilter((prev) => !prev)}
                    className={`p-2 rounded-lg ring-1 transition shrink-0 md:hidden ${showDateFilter ? "ring-teal-300 text-teal-700 bg-teal-50" : "ring-slate-200 text-slate-600 bg-white hover:bg-slate-50"}`}
                    title="Filter kategori / produk">
                    <Filter className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Mobile 3-Column Grid Source Sub-Filter for Transaction History (100% width, No Cut-Off) */}
            {activeReport === "transaction-history" && (
              <div className="p-2 sm:p-2.5 bg-slate-50/90 border-b border-slate-100 lg:hidden">
                <div className="grid grid-cols-3 gap-1.5 w-full">
                  {[
                    { id: "all", label: "Semua" },
                    { id: "POS", label: "Kasir POS" },
                    { id: "ECATALOG_PUBLIC", label: "E-Katalog" },
                  ].map((tab) => {
                    const active = (transactionSourceFilter || "all") === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setTransactionSourceFilter(tab.id)}
                        className={`py-1.5 px-1 sm:px-2 rounded-lg text-xs font-bold text-center transition-all border-0 cursor-pointer ${
                          active
                            ? "bg-slate-900 text-white shadow-xs"
                            : "bg-white text-slate-600 hover:bg-slate-100 ring-1 ring-slate-200/80"
                        }`}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Date filter (collapsible) */}
            {showDateFilter && (
              <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/40 space-y-4">
                {activeReport === "profit-loss" && (
                  <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3">
                    <button
                      onClick={handleResetFilter}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        reportFilterType === "all"
                          ? "bg-teal-600 text-white shadow-sm"
                          : "bg-white text-slate-600 hover:bg-slate-100 ring-1 ring-slate-200"
                      }`}>
                      Semua
                    </button>
                    <button
                      onClick={() => {
                        setReportFilterType("category");
                        setReportProductId("");
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        reportFilterType === "category"
                          ? "bg-teal-600 text-white shadow-sm"
                          : "bg-white text-slate-600 hover:bg-slate-100 ring-1 ring-slate-200"
                      }`}>
                      Kategori
                    </button>
                    <button
                      onClick={() => {
                        setReportFilterType("product");
                        setReportCategoryId("");
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        reportFilterType === "product"
                          ? "bg-teal-600 text-white shadow-sm"
                          : "bg-white text-slate-600 hover:bg-slate-100 ring-1 ring-slate-200"
                      }`}>
                      Produk
                    </button>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                  <div className="space-y-1">
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Tanggal Awal
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 bg-white ring-1 ring-slate-200 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Tanggal Akhir
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 py-2 bg-white ring-1 ring-slate-200 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors"
                    />
                  </div>

                  {activeReport === "profit-loss" && reportFilterType === "category" && (
                    <div className="space-y-1">
                      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Pilih Kategori
                      </label>
                      <SearchableSelect
                        options={categories}
                        value={reportCategoryId}
                        onChange={setReportCategoryId}
                        placeholder="Cari kategori..."
                      />
                    </div>
                  )}

                  {activeReport === "profit-loss" && reportFilterType === "product" && (
                    <div className="space-y-1">
                      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Pilih Produk
                      </label>
                      <SearchableSelect
                        options={products}
                        value={reportProductId}
                        onChange={setReportProductId}
                        placeholder="Cari produk..."
                      />
                    </div>
                  )}

                  <div className="flex gap-2 pb-[1px]">
                    <button
                      onClick={handleResetFilter}
                      className="flex-1 lg:flex-none px-5 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">
                      Reset
                    </button>
                    <button
                      onClick={handleApplyFilter}
                      className="flex-1 lg:flex-none px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm shadow-teal-600/20">
                      Terapkan
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeReport === "profit-sales" ? (
            <div className="p-4 sm:p-6">
              <ProfitSalesReportTab
                reportData={profitSalesData}
                loading={loading}
                searchTerm={searchTerm}
              />
            </div>
          ) : activeReport === "profit-loss" ? (
              <ProfitLossTable data={profitLossData} loading={loading} error={error} />
            ) : activeReport === "recap-sales-types" ? (
              <SalesTypeRecapTable
                rows={filteredRows}
                loading={loading}
                error={error}
                startDate={startDate}
                endDate={endDate}
                onSelectTransaction={(tx) => setSelectedTransaction(tx)}
              />
            ) : activeReport === "recap-payments" ? (
              <PaymentRecapTable
                rows={filteredRows}
                loading={loading}
                error={error}
                startDate={startDate}
                endDate={endDate}
                onSelectTransaction={(tx) => setSelectedTransaction(tx)}
              />
            ) : (
              <>
                <div className={`overflow-x-auto ${activeReport === "sales" || activeReport === "recap-units" ? "rounded-lg border border-slate-200" : ""}`}>
                  <table className={`min-w-full text-sm ${activeReport === "sales" || activeReport === "recap-units" ? "border-collapse border border-slate-200" : ""}`}>
                    <thead className={activeReport === "sales" || activeReport === "recap-units" ? "bg-slate-100/90" : "bg-slate-50/80 border-b border-slate-200"}>
                      <tr>
                        {showActionColumn && (
                          <th className={`px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 whitespace-nowrap ${
                            activeReport === "sales" || activeReport === "recap-units" ? "border border-slate-200" : ""
                          }`}>
                            Aksi
                          </th>
                        )}
                        {columns.map((col) => (
                          <th
                            key={col.key}
                            className={`px-4 py-3 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap ${
                              activeReport === "sales" || activeReport === "recap-units"
                                ? `text-slate-600 border border-slate-200 ${
                                    col.key === "product_name" || col.key === "unit" || col.key === "sparepart" || col.key === "name"
                                      ? "text-left"
                                      : col.key === "sub_total" || col.key === "total_pemakaian" || col.key === "price" || col.key === "total_per_nota" || col.key === "total_per_day" || col.type === "currency"
                                      ? "text-right"
                                      : "text-center"
                                  }`
                                : `text-slate-500 ${
                                    col.key === "product_name" || col.key === "name" || col.key === "unit" || col.key === "sparepart" ? "text-left" : "text-center"
                                  }`
                            }`}>
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className={activeReport === "sales" || activeReport === "recap-units" ? "bg-white" : "divide-y divide-slate-100 bg-white"}>
                      {loading && (
                        <tr>
                          <td
                            colSpan={
                              (columns.length || 1) + (showActionColumn ? 1 : 0)
                            }
                            className={`px-4 py-8 text-center text-slate-500 ${activeReport === "sales" || activeReport === "recap-units" ? "border border-slate-200" : ""}`}>
                            Memuat data laporan...
                          </td>
                        </tr>
                      )}

                      {!loading && error && (
                        <tr>
                          <td
                            colSpan={
                              (columns.length || 1) + (showActionColumn ? 1 : 0)
                            }
                            className={`px-4 py-8 text-center text-rose-600 ${activeReport === "sales" || activeReport === "recap-units" ? "border border-slate-200" : ""}`}>
                            {error}
                          </td>
                        </tr>
                      )}

                      {!loading &&
                        !error &&
                        filteredRows.length === 0 && (
                          <tr>
                            <td
                              colSpan={
                                (columns.length || 1) +
                                (showActionColumn ? 1 : 0)
                              }
                              className={`px-4 py-12 text-center ${activeReport === "sales" || activeReport === "recap-units" ? "border border-slate-200" : ""}`}>
                              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-50 mb-3 ring-1 ring-slate-100">
                                <Search className="w-5 h-5 text-slate-400" />
                              </div>
                              <p className="text-slate-500 font-medium">
                                Tidak ada data yang ditemukan
                              </p>
                              <p className="text-slate-400 text-sm mt-1">
                                Coba ubah filter atau kata kunci pencarian
                              </p>
                            </td>
                          </tr>
                        )}

                      {!loading &&
                        !error &&
                        renderRows.map(({ row, idx, spans, skip, isLastInDate }) => (
                          <tr
                            key={idx}
                            className={`hover:bg-slate-50/70 transition-colors ${
                              (activeReport === "sales" || activeReport === "recap-units") && isLastInDate ? "border-b-2 border-slate-300" : ""
                            }`}>
                            {showActionColumn && (
                              <td className={`px-4 py-3 whitespace-nowrap text-slate-700 align-middle text-center ${
                                activeReport === "sales" || activeReport === "recap-units" ? "border border-slate-200" : ""
                              }`}>
                                <button
                                  onClick={() => handleViewDetail(row)}
                                  disabled={!canShowDetail}
                                  title={
                                    canShowDetail
                                      ? "Lihat detail transaksi"
                                      : "Detail tidak tersedia untuk laporan rekap"
                                  }
                                  className="w-8 h-8 rounded-lg bg-teal-600 hover:bg-teal-700 text-white inline-flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-teal-600">
                                  <Info className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            )}
                            {columns.map((col) => {
                              if (skip[col.key]) return null;
                              const span = spans[col.key];
                              return (
                                <td
                                  key={`${idx}-${col.key}`}
                                  rowSpan={span || undefined}
                                  className={`px-4 py-3 whitespace-nowrap text-slate-700 align-middle ${
                                    col.key === "product_name" || col.key === "name" || col.key === "unit" || col.key === "sparepart"
                                      ? "text-left font-medium"
                                      : col.key === "sub_total" || col.key === "total_pemakaian" || col.key === "price" || col.key === "total_per_nota" || col.key === "total_per_day" || col.type === "currency"
                                      ? "text-right font-medium"
                                      : "text-center"
                                  } ${
                                    activeReport === "sales" || activeReport === "recap-units" ? "border border-slate-200" : ""
                                  } ${span ? "bg-slate-50/40 font-medium" : ""}`}>
                                  {formatCellValue(col, row[col.key], row)}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/40 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                    <span>Per Halaman</span>
                    <select
                      value={meta.limit}
                      onChange={(e) =>
                        fetchReport({ page: 1, limit: Number(e.target.value) })
                      }
                      className="px-2 py-1.5 bg-white ring-1 ring-slate-200 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                      {[10, 20, 50, 100, 200].map((limit) => (
                        <option key={limit} value={limit}>
                          {limit}
                        </option>
                      ))}
                    </select>
                    <span>
                      {formatNumber(startEntry)} - {formatNumber(endEntry)} dari
                      Total {formatNumber(meta.total)} Data
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => fetchReport({ page: 1 })}
                        disabled={meta.page <= 1 || loading}
                        className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50 inline-flex items-center justify-center transition"
                        title="Halaman pertama">
                        <ChevronsLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          fetchReport({ page: Math.max(1, meta.page - 1) })
                        }
                        disabled={meta.page <= 1 || loading}
                        className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50 inline-flex items-center justify-center transition"
                        title="Halaman sebelumnya">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="px-3 py-1 rounded-lg bg-teal-600 text-white text-sm font-semibold min-w-10 text-center shadow-sm shadow-teal-600/20">
                        {meta.page}
                      </span>
                      <button
                        onClick={() =>
                          fetchReport({ page: Math.min(maxPage, meta.page + 1) })
                        }
                        disabled={meta.page >= maxPage || loading}
                        className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50 inline-flex items-center justify-center transition"
                        title="Halaman berikutnya">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => fetchReport({ page: maxPage })}
                        disabled={meta.page >= maxPage || loading}
                        className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50 inline-flex items-center justify-center transition"
                        title="Halaman terakhir">
                        <ChevronsRight className="w-4 h-4" />
                      </button>
                    </div>

                    {totalSummary > 0 && (
                      <div className="px-3.5 py-1.5 rounded-xl bg-teal-50 text-teal-800 ring-1 ring-teal-200/70 font-bold text-xs whitespace-nowrap">
                        Total: {formatCurrency(totalSummary)}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {selectedTransaction && activeReport !== "shifts" && (
        <ReportTransactionDetailModal
          transaction={selectedTransaction}
          showSaleTypeDiscount={selectedTransaction?.showSaleTypeDiscount}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
      {selectedTransaction && activeReport === "shifts" && (
        <ReportShiftDetailModal
          shift={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
    </div>
  );
}

// === Summary Card Component ===
function SummaryCard({
  icon: Icon,
  label,
  value,
  hint,
  color = "blue",
  accent = false,
  loading = false,
}) {
  const palette = {
    blue: { bg: "bg-blue-50", text: "text-blue-600" },
    emerald: { bg: "bg-emerald-50", text: "text-emerald-600" },
    violet: { bg: "bg-violet-50", text: "text-violet-600" },
    amber: { bg: "bg-amber-50", text: "text-amber-600" },
    rose: { bg: "bg-rose-50", text: "text-rose-600" },
    teal: { bg: "bg-teal-50", text: "text-teal-600" },
    indigo: { bg: "bg-indigo-50", text: "text-indigo-600" },
    sky: { bg: "bg-sky-50", text: "text-sky-600" },
    pink: { bg: "bg-pink-50", text: "text-pink-600" },
  };
  const c = palette[color] || palette.blue;
  return (
    <div
      className={`relative bg-white p-5 rounded-2xl ring-1 ring-slate-200/70 shadow-sm hover:shadow-md hover:ring-slate-300/60 transition-all overflow-hidden ${
        accent
          ? "ring-emerald-200/70 bg-gradient-to-br from-white to-emerald-50/40"
          : ""
      }`}>
      <div className="flex items-start justify-between mb-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.bg} ${c.text}`}>
          {Icon ? <Icon className="w-5 h-5" /> : null}
        </div>
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </p>
      <div
        className={`text-xl font-bold text-slate-900 leading-tight truncate ${loading ? "opacity-50" : ""}`}>
        {loading ? "\u2026" : value}
      </div>
      {hint && (
        <div className="text-[11px] text-slate-400 mt-1 truncate">{hint}</div>
      )}
    </div>
  );
}

function ProfitLossTable({ data, loading, error }) {
  const calcPercent = (val, rev) => {
    if (!rev) return "0,00%";
    const pct = (val / rev) * 100;
    return pct.toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "%";
  };

  const d = data || {
    pendapatan: 0,
    diskon: 0,
    pajak: 0,
    pendapatan_bersih: 0,
    hpp: 0,
    biaya_operasional: 0,
    total_biaya: 0,
    laba_bersih: 0
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-500 text-sm">Memuat data...</div>;
  }

  if (error) {
    return <div className="p-8 text-center text-rose-500 text-sm">{error}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50/80 border-b border-slate-200">
          <tr>
            <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Uraian</th>
            <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Jumlah</th>
            <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">% Terhadap Pendapatan</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <tr className="hover:bg-slate-50/70">
            <td className="px-5 py-3 text-slate-700 font-medium">Pendapatan</td>
            <td className="px-5 py-3 text-right text-slate-700">{formatCurrency(d.pendapatan)}</td>
            <td className="px-5 py-3 text-right text-slate-500">{calcPercent(d.pendapatan, d.pendapatan)}</td>
          </tr>
          <tr className="hover:bg-slate-50/70">
            <td className="px-5 py-3 text-slate-700 font-medium">Diskon</td>
            <td className="px-5 py-3 text-right text-rose-600">- {formatCurrency(d.diskon)}</td>
            <td className="px-5 py-3 text-right text-rose-500">-{calcPercent(d.diskon, d.pendapatan)}</td>
          </tr>
          <tr className="hover:bg-slate-50/70">
            <td className="px-5 py-3 text-slate-700 font-medium">Pajak</td>
            <td className="px-5 py-3 text-right text-rose-600">- {formatCurrency(d.pajak)}</td>
            <td className="px-5 py-3 text-right text-rose-500">-{calcPercent(d.pajak, d.pendapatan)}</td>
          </tr>
          <tr className="bg-slate-50/50 hover:bg-slate-50">
            <td className="px-5 py-3 text-slate-800 font-bold">Pendapatan Bersih</td>
            <td className="px-5 py-3 text-right text-slate-800 font-bold">{formatCurrency(d.pendapatan_bersih)}</td>
            <td className="px-5 py-3 text-right text-slate-600 font-medium">{calcPercent(d.pendapatan_bersih, d.pendapatan)}</td>
          </tr>
          <tr className="hover:bg-slate-50/70">
            <td className="px-5 py-3 text-slate-700 font-medium">Total HPP</td>
            <td className="px-5 py-3 text-right text-rose-600">- {formatCurrency(d.hpp)}</td>
            <td className="px-5 py-3 text-right text-rose-500">-{calcPercent(d.hpp, d.pendapatan)}</td>
          </tr>
          <tr className="hover:bg-slate-50/70">
            <td className="px-5 py-3 text-slate-700 font-medium">Biaya Operasional</td>
            <td className="px-5 py-3 text-right text-rose-600">- {formatCurrency(d.biaya_operasional)}</td>
            <td className="px-5 py-3 text-right text-rose-500">-{calcPercent(d.biaya_operasional, d.pendapatan)}</td>
          </tr>
          <tr className="bg-slate-50/50 hover:bg-slate-50">
            <td className="px-5 py-3 text-slate-800 font-bold">Total Biaya</td>
            <td className="px-5 py-3 text-right text-rose-600 font-bold">- {formatCurrency(d.total_biaya)}</td>
            <td className="px-5 py-3 text-right text-rose-600 font-medium">-{calcPercent(d.total_biaya, d.pendapatan)}</td>
          </tr>
          <tr className="bg-emerald-50/30 hover:bg-emerald-50/50">
            <td className="px-5 py-3 text-emerald-700 font-bold">Laba Bersih</td>
            <td className="px-5 py-3 text-right text-emerald-700 font-bold">{formatCurrency(d.laba_bersih)}</td>
            <td className="px-5 py-3 text-right text-emerald-600 font-bold">{calcPercent(d.laba_bersih, d.pendapatan)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
